import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createDebuggableSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {
  createTableEditableStoreStructuredSelector,
  TableEditableStore,
} from 'notebook/tables/store/TableStore';

/**
 * The table-level values a row renders with. These are identical for every row, but they are
 * relayed through the row model so that a row has everything it needs to render in one place
 * rather than reaching back into the {@link TableModel} for each value individually.
 */
export interface RowTableModel {
  /** The table's own cid, used to build the `data-cid` of the cells on this row. */
  cid: string;
  /** Whether the table as a whole is locked. */
  isTableLocked: boolean;
  /** Whether the table is protected, which restricts the row axis cell's context menu. */
  isProtected: boolean;
  /**
   * Whether the selection covers every row, which makes the row axis cell offer "Delete table"
   * instead of "Delete rows".
   */
  isEveryRowSelected: boolean;
  /** Whether the row axis cells should render excess labels. */
  showRowExcessLabels: boolean;
  /** Whether the row header renders left-aligned. */
  isLeftAlignRowHeader: boolean;
  /** Whether table dimensions can be modified, which gates toggling the header row. */
  canModifyDims: boolean;
  /** Whether rows can be inserted. */
  canModifyRowDim: boolean;
  /**
   * The number of ghost cells to render after the row's real cells. Always zero in the
   * single-input view, which has no ghost cells.
   */
  ghostCellsCount: number;
  /** The number of rows in the table. */
  rowCount: number;
  /** Whether the table is allowed to have zero rows. */
  allowNoRows: boolean;
}

/**
 * Creates a selector that collects the table-level values a single row renders with
 * ({@link RowTableModel}) from the {@link TableModel}.
 */
export function createRowTableSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  isSingleInput: boolean
): (store: TableEditableStore) => RowTableModel {
  return createTableEditableStoreStructuredSelector(
    {
      cid: (store) => tableSelector(store).props.cid,
      isTableLocked: (store) => tableSelector(store).locking.isTableLocked,
      // `props` holds TableEditable's raw props, so the `??` defaults here repeat the ones
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      isProtected: (store) => tableSelector(store).props.isProtected ?? false,
      isEveryRowSelected: (store) => tableSelector(store).selection.isEveryRowSelected,
      showRowExcessLabels: (store) => tableSelector(store).visibility.showRowExcessLabels,
      isLeftAlignRowHeader: (store) => tableSelector(store).props.isLeftAlignRowHeader ?? false,
      canModifyDims: (store) => tableSelector(store).dimensions.canModifyDims,
      canModifyRowDim: (store) => tableSelector(store).dimensions.canModifyRowDim,
      ghostCellsCount: (store) => (isSingleInput ? 0 : tableSelector(store).dimensions.ghostCellsCount),
      rowCount: (store) => tableSelector(store).dimensions.rowCount,
      allowNoRows: (store) => tableSelector(store).dimensions.allowNoRows,
    },
    createDebuggableSelector.withTypes<TableEditableStore>()
  );
}
